import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { useNotesStore } from './notesStore';
import {
  fetchNotesBootstrap,
  fetchNotes,
  fetchNote as apiFetchNote,
  createNote as apiCreateNote,
  deleteNote as apiDeleteNote,
  restoreNote as apiRestoreNote,
  archiveNote as apiArchiveNote,
  closeNote as apiCloseNote,
  setNotePinned as apiSetNotePinned,
  getActiveSession as apiGetActiveSession,
  searchNotes as apiSearchNotes,
  createNoteTag as apiCreateNoteTag,
  updateNoteTag as apiUpdateNoteTag,
  deleteNoteTag as apiDeleteNoteTag,
  updateNoteTags as apiUpdateNoteTags,
  linkNoteTask as apiLinkNoteTask,
  unlinkNoteTask as apiUnlinkNoteTask,
} from '@/api/notesApi';
import {
  queueNoteSave,
  saveNoteNow,
  flushNoteSave,
  flushAllSaves,
  hasPendingSave,
  hasAnyPendingSaves,
  discardPendingSave,
  subscribeNoteSaves,
} from '@/lib/noteSaveQueue';
import type { Note, NoteTagFull, ActiveSessionResponse, NoteSearchResult } from '@/types/note';

// Re-export save queue utilities for use by components
export {
  queueNoteSave,
  saveNoteNow,
  flushNoteSave,
  flushAllSaves,
  hasPendingSave,
  hasAnyPendingSaves,
  discardPendingSave,
};

export type NotesView = 'active' | 'archive';

interface NotesActionsState {
  activeNoteId: string | null;
  selectedTagId: string | null;
  selectedView: NotesView;
  searchQuery: string;
  searchResults: NoteSearchResult[];
  isSearching: boolean;

  // The open scratchpad session note (closedAt === null), if any
  activeSessionNote: Note | null;

  // Actions
  setActiveNoteId: (noteId: string | null) => void;
  setSelectedTagId: (tagId: string | null) => void;
  setSelectedView: (view: NotesView) => void;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  setActiveSessionNote: (note: Note | null) => void;
  clearActiveSession: () => void;
}

export const useNotesActionsStore = create<NotesActionsState>()(
  conditionalDevtools(
    (set) => ({
      activeNoteId: null,
      selectedTagId: null,
      selectedView: 'active',
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      activeSessionNote: null,

      setActiveNoteId: (noteId) => set({ activeNoteId: noteId }),
      setSelectedTagId: (tagId) => set({ selectedTagId: tagId }),
      setSelectedView: (view) => set({ selectedView: view }),
      setSearchQuery: (query) => set({ searchQuery: query }),
      clearSearch: () => set({ searchQuery: '', searchResults: [], isSearching: false }),
      setActiveSessionNote: (note) => set({ activeSessionNote: note }),
      clearActiveSession: () => set({ activeSessionNote: null }),
    }),
    { name: 'NotesActionsStore' }
  )
);

// Every successful queue save flows back into the store from one place (I4):
// no component needs its own "save then upsert" logic.
subscribeNoteSaves((event) => {
  if (event.phase !== 'saved' || !event.note) {
    return;
  }
  useNotesStore.getState().upsertNote(event.note);
  const actionsStore = useNotesActionsStore.getState();
  if (actionsStore.activeSessionNote?.id === event.note.id) {
    actionsStore.setActiveSessionNote(event.note);
  }
});

// Action functions (not in store to avoid circular deps and for easier testing)

export async function bootstrapNotes(): Promise<void> {
  const store = useNotesStore.getState();
  if (store.initialized && !store.bootstrapFailed) {
    return;
  }

  store.setLoading(true);
  try {
    const payload = await fetchNotesBootstrap();
    store.hydrate(payload);
  } catch (error) {
    console.error('Failed to bootstrap notes:', error);
    store.setBootstrapFailed(true);
    store.setLoading(false);
    throw error;
  }
}

export async function loadMoreNotes(): Promise<void> {
  const store = useNotesStore.getState();
  if (!store.hasMore || store.loading) {
    return;
  }

  store.setLoading(true);
  try {
    const offset = store.loadedCount;
    const { notes, total, hasMore } = await fetchNotes({ offset, limit: 50 });
    store.appendNotes(notes, total, hasMore);
  } catch (error) {
    console.error('Failed to load more notes:', error);
  } finally {
    store.setLoading(false);
  }
}

/**
 * Fetch a note by id into the store (deep links to notes beyond the
 * paginated window). Returns null when the note doesn't exist.
 */
export async function ensureNoteLoaded(noteId: string): Promise<Note | null> {
  const existing = useNotesStore.getState().notes[noteId];
  if (existing) {
    return existing;
  }
  try {
    const note = await apiFetchNote(noteId);
    useNotesStore.getState().upsertNote(note);
    return note;
  } catch (error) {
    console.error(`Failed to load note ${noteId}:`, error);
    return null;
  }
}

export async function createNote(
  payload: { contentMarkdown?: string; closed?: boolean } = {},
  options?: { openEditor?: boolean }
): Promise<Note> {
  const note = await apiCreateNote(payload);
  useNotesStore.getState().upsertNote(note);
  if (options?.openEditor !== false) {
    useNotesActionsStore.getState().setActiveNoteId(note.id);
  }
  return note;
}

/**
 * Create a new open session note (scratchpad). Called on the first
 * keystroke in an empty scratchpad.
 */
export async function createSessionNote(initialContent: string): Promise<Note> {
  const note = await apiCreateNote({ contentMarkdown: initialContent, closed: false });
  useNotesStore.getState().upsertNote(note);
  useNotesActionsStore.getState().setActiveSessionNote(note);
  return note;
}

/**
 * Close the scratchpad session note (graduate it to the grid). The server
 * deletes empty notes instead of closing them; both outcomes clear the pad.
 * AI organizing runs server-side after close.
 */
export async function closeSessionNote(noteId: string): Promise<void> {
  await flushNoteSave(noteId);
  const result = await apiCloseNote(noteId);
  const store = useNotesStore.getState();
  if (result.deleted) {
    store.removeNote(noteId);
  } else if (result.note) {
    store.upsertNote(result.note);
  }
  const actionsStore = useNotesActionsStore.getState();
  if (actionsStore.activeSessionNote?.id === noteId) {
    actionsStore.clearActiveSession();
  }
}

export async function setNotePinned(noteId: string, pinned: boolean): Promise<Note> {
  const note = await apiSetNotePinned(noteId, pinned);
  useNotesStore.getState().upsertNote(note);
  const actionsStore = useNotesActionsStore.getState();
  if (actionsStore.activeSessionNote?.id === noteId) {
    actionsStore.setActiveSessionNote(note);
  }
  return note;
}

/**
 * Immediately save a note update and wait for completion.
 */
export async function saveNoteImmediately(
  noteId: string,
  updates: { contentMarkdown?: string; title?: string | null }
): Promise<Note> {
  // The queue's save listener handles the store/session sync.
  return saveNoteNow(noteId, updates);
}

/**
 * Flush any pending save for a specific note (store sync via save listener).
 */
export async function flushAndSyncNote(noteId: string): Promise<Note | null> {
  return flushNoteSave(noteId);
}

export async function deleteNote(noteId: string, hard = false): Promise<void> {
  // Don't let a queued patch resurrect (or 404 against) the deleted note.
  discardPendingSave(noteId);
  const result = await apiDeleteNote(noteId, hard);
  if (hard || result.deleted) {
    useNotesStore.getState().removeNote(noteId);
  }

  const actionsStore = useNotesActionsStore.getState();
  if (actionsStore.activeNoteId === noteId) {
    actionsStore.setActiveNoteId(null);
  }
  if (actionsStore.activeSessionNote?.id === noteId) {
    actionsStore.clearActiveSession();
  }
}

export async function restoreNote(noteId: string): Promise<Note> {
  const note = await apiRestoreNote(noteId);
  useNotesStore.getState().upsertNote(note);
  return note;
}

export async function archiveNote(noteId: string): Promise<Note> {
  const note = await apiArchiveNote(noteId);
  useNotesStore.getState().upsertNote(note);
  return note;
}

export async function checkActiveSession(): Promise<ActiveSessionResponse> {
  const response = await apiGetActiveSession();

  if (response.hasActiveSession && response.note) {
    useNotesStore.getState().upsertNote(response.note);
    useNotesActionsStore.getState().setActiveSessionNote(response.note);
  }

  return response;
}

export async function searchNotes(query: string): Promise<NoteSearchResult[]> {
  if (!query.trim()) {
    useNotesActionsStore.setState({ searchResults: [], isSearching: false });
    return [];
  }

  useNotesActionsStore.setState({ isSearching: true });
  try {
    const { results } = await apiSearchNotes(query.trim(), { limit: 50 });
    // Results are full notes; keep the store warm so opening one just works.
    useNotesStore.getState().upsertNotes(results);
    useNotesActionsStore.setState({ searchResults: results, isSearching: false });
    return results;
  } catch (error) {
    console.error('Search failed:', error);
    useNotesActionsStore.setState({ isSearching: false });
    throw error;
  }
}

// Tags

export async function createTag(payload: { name: string; color?: string | null }): Promise<NoteTagFull> {
  const tag = await apiCreateNoteTag(payload);
  useNotesStore.getState().upsertTag(tag);
  return tag;
}

export async function updateTag(
  tagId: string,
  updates: { name?: string; color?: string | null }
): Promise<NoteTagFull> {
  const tag = await apiUpdateNoteTag(tagId, updates);
  useNotesStore.getState().upsertTag(tag);
  return tag;
}

export async function deleteTag(tagId: string): Promise<void> {
  await apiDeleteNoteTag(tagId);
  useNotesStore.getState().removeTag(tagId);
}

// Note-Tag assignments

export async function updateNoteTags(noteId: string, tagIds: string[]): Promise<Note> {
  const note = await apiUpdateNoteTags(noteId, tagIds);
  useNotesStore.getState().upsertNote(note);
  return note;
}

// Note-Task links

export async function linkTask(
  noteId: string,
  taskId: string,
  options?: { source?: string; sourceText?: string; confidence?: number }
): Promise<void> {
  await apiLinkNoteTask(noteId, { taskId, ...options });
}

export async function unlinkTask(noteId: string, taskId: string): Promise<void> {
  await apiUnlinkNoteTask(noteId, taskId);
}
